import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

import { useExtractionApi } from "@/hooks/useExtractionApi";
import { CountrySelector } from "@/components/CountrySelector";
import { FirstLevelSelector } from "@/components/FirstLevelSelector";
import { SecondLevelSelector } from "@/components/SecondLevelSelector";
import { ConfirmExtraction } from "@/components/ConfirmExtraction";
import { ExtractionStatus } from "@/components/ExtractionStatus";
import { ExtractionHistory } from "@/components/ExtractionHistory";
import { DataVerification } from "@/components/DataVerification";
import { AICuration } from "@/components/AICuration";

const CONTINENTS = ["North America", "South America", "Europe", "Asia", "Africa", "Oceania"];

function App() {
  const api = useExtractionApi();

  // --- Core state ---
  const [activeTab, setActiveTab] = useState("curation");
  const [step, setStep] = useState(1);
  const [selectedContinent, setSelectedContinent] = useState("");
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);  // Multi-select countries
  const [selectedCountry, setSelectedCountry] = useState("");       // Currently active country (for state drill-down)
  const [selectedCountryInfo, setSelectedCountryInfo] = useState(null);

  // First Level (multi-select)
  const [isAnalyzingFirst, setIsAnalyzingFirst] = useState(false);
  const [firstLevelAnalysis, setFirstLevelAnalysis] = useState(null);
  const [selectedFirstLevels, setSelectedFirstLevels] = useState([]);

  // Second Level
  const [isAnalyzingSecond, setIsAnalyzingSecond] = useState(false);
  const [secondLevelAnalysis, setSecondLevelAnalysis] = useState(null);
  const [selectedSecondLevels, setSelectedSecondLevels] = useState([]);

  // Extraction state
  const [extractionStatus, setExtractionStatus] = useState({ is_running: false, progress: [], last_error: null });
  const [extractionRuns, setExtractionRuns] = useState([]);
  const [completedExtractions, setCompletedExtractions] = useState([]);
  const [stats, setStats] = useState({ total_beaches: 0, by_continent: {}, by_country: {} });
  const [wasRunning, setWasRunning] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [dbHealth, setDbHealth] = useState(null);

  // Refs for polling callbacks (avoids stale closures in useEffect)
  const wasRunningRef = useRef(wasRunning);
  wasRunningRef.current = wasRunning;

  // --- Initial load ---
  useEffect(() => {
    api.fetchStats(setStats);
    api.fetchExtractionRuns(setExtractionRuns);
    api.fetchCompletedExtractions(setCompletedExtractions);
    api.checkHealth(setDbHealth);
  }, [api]);

  // --- Country fetch on continent change ---
  useEffect(() => {
    if (selectedContinent) {
      api.fetchCountries(selectedContinent, setCountries);
      resetFromStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContinent]);

  // --- Extraction status polling ---
  const pollStatus = useCallback(async () => {
    const data = await api.fetchExtractionStatus();
    if (!data) return;

    // Detect completion/failure
    if (wasRunningRef.current && !data.is_running) {
      api.fetchStats(setStats);
      api.fetchExtractionRuns(setExtractionRuns);
      api.fetchCompletedExtractions(setCompletedExtractions);

      if (data.last_error) {
        const labels = {
          oom: "Out of Memory", download_error: "Download Failed",
          server_busy: "Server Busy", download_timeout: "Timed Out",
          download_exhausted: "Retries Exhausted", connection_error: "Connection Error",
          parse_error: "Extraction Error",
        };
        toast.error(`Extraction failed: ${labels[data.last_error.error_type] || data.last_error.error_type}`, { duration: 8000 });
      } else {
        toast.success("Extraction completed successfully!", { duration: 5000 });
      }
    }

    setWasRunning(data.is_running);
    setExtractionStatus(data);
  }, [api]);

  useEffect(() => {
    if (!extractionStatus.is_running && !wasRunning) return;
    const interval = setInterval(pollStatus, 2000);
    return () => clearInterval(interval);
  }, [extractionStatus.is_running, wasRunning, pollStatus]);

  // --- Workflow helpers ---
  const resetFromStep = (fromStep) => {
    if (fromStep <= 2) { setSelectedCountries([]); setSelectedCountry(""); setSelectedCountryInfo(null); }
    if (fromStep <= 3) { setFirstLevelAnalysis(null); setSelectedFirstLevels([]); }
    if (fromStep <= 4) { setSecondLevelAnalysis(null); setSelectedSecondLevels([]); }
    setStep(fromStep);
  };

  // Country multi-select
  const toggleCountry = (countryObj) => {
    setSelectedCountries((prev) =>
      prev.some((c) => c.name === countryObj.name)
        ? prev.filter((c) => c.name !== countryObj.name)
        : [...prev, countryObj]
    );
  };

  const selectAllCountries = () => setSelectedCountries([...countries]);

  const selectPendingCountries = () => {
    const pending = countries.filter(
      (c) => !completedExtractions.some((ce) => ce.country === c.name)
    );
    setSelectedCountries(pending);
  };

  // When user picks exactly 1 country → drill into states
  const proceedSingleCountry = (countryObj) => {
    setSelectedCountry(countryObj.name);
    setSelectedCountryInfo(countryObj);
    setFirstLevelAnalysis(null);
    setSelectedFirstLevels([]);
    setSecondLevelAnalysis(null);
    setSelectedSecondLevels([]);
    setStep(3);
  };

  // When user picks multiple countries → go straight to confirm
  const proceedMultiCountry = () => setStep(5);

  // Start multi-country extraction
  const handleStartMultiCountry = async () => {
    if (selectedCountries.length < 2) return;
    try {
      const data = await api.startExtraction({
        continent: selectedContinent,
        country: selectedCountries[0].name,
        countries: selectedCountries.map((c) => ({ name: c.name, geofabrik_path: c.geofabrik_path })),
        geofabrik_path: selectedCountries[0].geofabrik_path,
      });
      toast.success(data.message, { duration: 6000 });
      setWasRunning(true);
      pollStatus();
      setStep(6);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
  };

  const handleAnalyzeFirst = async () => {
    if (!selectedCountry) return;
    setIsAnalyzingFirst(true);
    try {
      const data = await api.analyzeFirstLevel(selectedCountry);
      setFirstLevelAnalysis(data);
    } catch (e) {
      toast.error("Analysis failed: " + (e.response?.data?.detail || e.message));
    }
    setIsAnalyzingFirst(false);
  };

  const handleClearAnalysis = async () => {
    try {
      await api.clearAnalysis(selectedCountry);
      setFirstLevelAnalysis(null);
      setSecondLevelAnalysis(null);
    } catch (e) {
      console.error(e);
    }
  };

  // First-level multi-select
  const toggleFirstLevel = (sub) => {
    const name = sub.name || sub;
    setSelectedFirstLevels((prev) =>
      prev.some((s) => (s.name || s) === name) ? prev.filter((s) => (s.name || s) !== name) : [...prev, sub]
    );
  };

  const selectAllFirstLevels = () => {
    if (firstLevelAnalysis?.coastal_subdivisions) {
      setSelectedFirstLevels([...firstLevelAnalysis.coastal_subdivisions]);
    }
  };

  const selectPendingFirstLevels = () => {
    if (!firstLevelAnalysis?.coastal_subdivisions) return;
    const pending = firstLevelAnalysis.coastal_subdivisions.filter((sub) => {
      const name = sub.name || sub;
      return !completedExtractions.some((c) => c.country === selectedCountry && c.first_level === name);
    });
    setSelectedFirstLevels(pending);
  };

  const proceedFromFirstLevel = () => setStep(4);

  // Second-level
  const handleAnalyzeSecond = async () => {
    if (!selectedFirstLevels.length) return;
    setIsAnalyzingSecond(true);
    try {
      const data = await api.analyzeSecondLevel(selectedCountry, selectedFirstLevels[0].name);
      setSecondLevelAnalysis(data);
    } catch (e) {
      toast.error("Analysis failed: " + (e.response?.data?.detail || e.message));
    }
    setIsAnalyzingSecond(false);
  };

  const toggleSecondLevel = (name) =>
    setSelectedSecondLevels((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));

  const selectAllSecondLevel = () => {
    if (secondLevelAnalysis?.coastal_subdivisions) {
      setSelectedSecondLevels(secondLevelAnalysis.coastal_subdivisions.map((s) => s.name || s));
    }
  };

  // Extraction
  const handleStartExtraction = async () => {
    if (!selectedCountryInfo) return;
    try {
      const firstLevelNames = selectedFirstLevels.map((s) => s.name || s);
      const data = await api.startExtraction({
        continent: selectedContinent,
        country: selectedCountry,
        first_level_name: firstLevelNames.length === 1 ? firstLevelNames[0] : null,
        first_level_names: firstLevelNames.length > 1 ? firstLevelNames : null,
        second_level_names: selectedSecondLevels.length > 0 ? selectedSecondLevels : null,
        geofabrik_path: selectedCountryInfo.geofabrik_path,
      });
      toast.success(data.message, { duration: 6000 });
      setWasRunning(true);
      pollStatus();
      setStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
  };

  const handleRetry = async (runId) => {
    setRetryingId(runId);
    try {
      const data = await api.retryExtraction(runId);
      toast.info(data.message);
      setWasRunning(true);
      pollStatus();
    } catch (e) {
      toast.error("Retry failed: " + (e.response?.data?.detail || e.message));
    }
    setRetryingId(null);
  };

  const dismissError = () => setExtractionStatus((prev) => ({ ...prev, last_error: null }));

  // --- Derived booleans ---
  const showFirstLevelInfo = firstLevelAnalysis && selectedFirstLevels.length === 0;
  const showFirstLevelGrid = firstLevelAnalysis && selectedFirstLevels.length === 0 && step === 3;
  const showDrillDown = selectedFirstLevels.length === 1 && selectedFirstLevels[0].needs_drilldown && !secondLevelAnalysis && step === 4;
  const showSecondLevelGrid = !!secondLevelAnalysis;
  const showConfirm =
    selectedFirstLevels.length > 0 &&
    (selectedFirstLevels.length > 1 || !selectedFirstLevels[0].needs_drilldown || selectedSecondLevels.length > 0) &&
    !extractionStatus.is_running &&
    step >= 4;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Toaster position="top-right" />

      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-semibold">OnlyBeaches</h1>
              <p className="text-xs text-slate-500">AI-Powered Hierarchical Extraction</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {dbHealth && dbHealth.database !== "connected" && (
              <Badge data-testid="db-warning-badge" className="bg-red-500/20 text-red-400 border-red-500/30 animate-pulse">DB Disconnected</Badge>
            )}
            <Badge data-testid="total-beaches-badge" variant="outline" className="border-cyan-500/50 text-cyan-400">{stats.total_beaches?.toLocaleString() || 0} Beaches</Badge>
          </div>
        </div>
      </header>

      {/* DB Warning Banner */}
      {dbHealth && dbHealth.database !== "connected" && (
        <div data-testid="db-warning-banner" className="border-b border-amber-500/30 bg-amber-950/40 px-6 py-3">
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <svg className="w-5 h-5 text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            <div className="flex-1">
              <span className="font-medium text-amber-300">Database not connected</span>
              <span className="text-amber-400/70 text-sm ml-2">{dbHealth.message}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => api.checkHealth(setDbHealth)} className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs">Retry</Button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-6 flex gap-6">
        {/* Sidebar */}
        <aside className="w-72 space-y-4">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Database Stats</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-cyan-400 mb-3">{stats.total_beaches?.toLocaleString() || 0}</div>
              {CONTINENTS.map((c) => (
                <div key={c} className="flex justify-between text-sm py-0.5">
                  <span className="text-slate-500">{c}</span>
                  <span className="font-mono text-slate-300">{(stats.by_continent?.[c] || 0).toLocaleString()}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-400">Completed</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-40">
                {completedExtractions.length > 0 ? completedExtractions.map((c) => (
                  <div key={`comp-${c.country}-${c.first_level}`} className="flex items-center gap-2 text-sm py-1">
                    <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    <span className="truncate">{c.country}{c.first_level ? ` / ${c.first_level}` : ""}</span>
                    <span className="text-slate-500 ml-auto">{c.beaches_count}</span>
                  </div>
                )) : <div className="text-slate-500 text-sm">None yet</div>}
              </ScrollArea>
            </CardContent>
          </Card>
        </aside>

        {/* Main */}
        <main className="flex-1">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-slate-900 border border-slate-800 mb-6">
              <TabsTrigger data-testid="tab-curation" value="curation" className="data-[state=active]:bg-slate-800">AI Beach Curation</TabsTrigger>
              <TabsTrigger data-testid="tab-extraction" value="extraction" className="data-[state=active]:bg-slate-800">OSM Extraction</TabsTrigger>
              <TabsTrigger data-testid="tab-verification" value="verification" className="data-[state=active]:bg-slate-800">Data Verification</TabsTrigger>
            </TabsList>

            <TabsContent value="curation">
              <AICuration />
            </TabsContent>

            <TabsContent value="extraction" className="space-y-6">
              {/* Step Indicator */}
              <div className="flex items-center gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((s) => (
                  <div key={`step-${s}`} className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? "bg-cyan-500 text-white" : "bg-slate-800 text-slate-500"}`}>{s}</div>
                    {s < 5 && <div className={`w-8 h-0.5 ${step > s ? "bg-cyan-500" : "bg-slate-800"}`} />}
                  </div>
                ))}
                <span className="ml-4 text-sm text-slate-400">
                  {step === 1 && "Select Continent"}
                  {step === 2 && "Select Country"}
                  {step === 3 && "Select State/Province"}
                  {step === 4 && "Select Counties/Districts (if needed)"}
                  {step === 5 && "Extraction Running"}
                </span>
                {step > 1 && (
                  <Button data-testid="btn-reset" variant="ghost" size="sm" onClick={() => { resetFromStep(1); setSelectedContinent(""); }} className="ml-auto text-slate-400">Reset</Button>
                )}
              </div>

              {/* Error Banner */}
              {extractionStatus.last_error && !extractionStatus.is_running && (
                <ErrorBanner error={extractionStatus.last_error} onDismiss={dismissError} />
              )}

              <div className="grid grid-cols-2 gap-6">
                {/* Selection Panel */}
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardHeader>
                    <CardTitle>Hierarchical Selection</CardTitle>
                    <CardDescription>Continent → Country → State → County</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Step 1: Continent */}
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">1. Continent</label>
                      <Select value={selectedContinent} onValueChange={setSelectedContinent}>
                        <SelectTrigger data-testid="select-continent" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Select continent" /></SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {CONTINENTS.map((c) => <SelectItem key={c} value={c} data-testid={`continent-${c.replace(/\s/g, "-").toLowerCase()}`}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Step 2: Country (multi-select) */}
                    {selectedContinent && countries.length > 0 && !selectedCountry && (
                      <CountrySelector
                        countries={countries}
                        selectedCountries={selectedCountries}
                        completedExtractions={completedExtractions}
                        onToggle={toggleCountry}
                        onSelectAll={selectAllCountries}
                        onSelectPending={selectPendingCountries}
                        onClear={() => setSelectedCountries([])}
                        onProceedSingle={proceedSingleCountry}
                        onProceedMulti={proceedMultiCountry}
                      />
                    )}

                    {/* Selected country summary (single country drill-down mode) */}
                    {selectedCountry && (
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-cyan-400 font-medium">{selectedCountry}</span>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedCountry(""); setSelectedCountryInfo(null); resetFromStep(2); }} className="text-xs">Change</Button>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Analyze */}
                    {selectedCountry && !firstLevelAnalysis && (
                      <Button data-testid="btn-analyze-first" onClick={handleAnalyzeFirst} disabled={isAnalyzingFirst} className="w-full bg-cyan-600 hover:bg-cyan-500">
                        {isAnalyzingFirst ? "AI Analyzing States/Provinces..." : `Analyze ${selectedCountry} (First Level)`}
                      </Button>
                    )}

                    {/* First Level Info */}
                    {showFirstLevelInfo && (
                      <div className="space-y-3" data-testid="first-level-info">
                        <div className="bg-slate-800/50 rounded-lg p-3 text-sm">
                          <div className="flex justify-between mb-1">
                            <span className="text-slate-400">Type:</span>
                            <Badge variant="outline" className="text-cyan-400">{firstLevelAnalysis.subdivision_type}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Coastal Units:</span>
                            <span data-testid="coastal-units-count">{firstLevelAnalysis.coastal_subdivisions?.length || 0}</span>
                          </div>
                          {firstLevelAnalysis.use_second_level_if_needed && (
                            <div className="text-xs text-amber-400 mt-2">Some units need drill-down to {firstLevelAnalysis.second_level_type}</div>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 bg-slate-800/30 rounded p-2">{firstLevelAnalysis.reasoning}</div>
                        <div className="flex gap-2">
                          <Button data-testid="btn-reanalyze" variant="outline" size="sm" onClick={handleClearAnalysis} className="border-slate-700">Re-analyze</Button>
                          {firstLevelAnalysis.cached && <Badge variant="outline" className="text-amber-400">Cached</Badge>}
                        </div>
                      </div>
                    )}

                    {/* Selected Summary */}
                    {selectedFirstLevels.length > 0 && (
                      <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-cyan-400 font-medium">{selectedFirstLevels.length} selected</span>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedFirstLevels([]); setSecondLevelAnalysis(null); setStep(3); }} className="text-xs">Change</Button>
                        </div>
                        <div className="text-xs text-slate-400 truncate">{selectedFirstLevels.map((s) => s.name || s).join(", ")}</div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Status Panel */}
                <ExtractionStatus extractionStatus={extractionStatus} />
              </div>

              {/* First Level Grid */}
              {showFirstLevelGrid && (
                <FirstLevelSelector
                  analysis={firstLevelAnalysis}
                  selectedFirstLevels={selectedFirstLevels}
                  completedExtractions={completedExtractions}
                  selectedCountry={selectedCountry}
                  onToggle={toggleFirstLevel}
                  onSelectAll={selectAllFirstLevels}
                  onSelectPending={selectPendingFirstLevels}
                  onClear={() => setSelectedFirstLevels([])}
                  onProceed={proceedFromFirstLevel}
                />
              )}

              {/* Second Level Analyze */}
              {showDrillDown && (
                <Card className="bg-slate-900/50 border-slate-800" data-testid="second-level-analyze-card">
                  <CardHeader>
                    <CardTitle>Step 4: Drill Down to Counties</CardTitle>
                    <CardDescription>{selectedFirstLevels[0].name} is large - select specific counties</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button data-testid="btn-analyze-second" onClick={handleAnalyzeSecond} disabled={isAnalyzingSecond} className="w-full bg-cyan-600 hover:bg-cyan-500">
                      {isAnalyzingSecond ? "AI Analyzing Counties..." : `Analyze ${selectedFirstLevels[0].name} Counties`}
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Second Level Grid */}
              {showSecondLevelGrid && (
                <SecondLevelSelector
                  analysis={secondLevelAnalysis}
                  selectedSecondLevels={selectedSecondLevels}
                  onToggle={toggleSecondLevel}
                  onSelectAll={selectAllSecondLevel}
                  onClear={() => setSelectedSecondLevels([])}
                />
              )}

              {/* Confirm */}
              {showConfirm && (
                <ConfirmExtraction
                  selectedCountry={selectedCountry}
                  selectedFirstLevels={selectedFirstLevels}
                  selectedSecondLevels={selectedSecondLevels}
                  firstLevelType={firstLevelAnalysis?.subdivision_type}
                  secondLevelType={secondLevelAnalysis?.subdivision_type}
                  completedExtractions={completedExtractions}
                  onConfirm={handleStartExtraction}
                />
              )}

              {/* Multi-Country Confirm */}
              {selectedCountries.length > 1 && step === 5 && !extractionStatus.is_running && (
                <Card className="bg-slate-900/50 border-slate-800" data-testid="multi-country-confirm-card">
                  <CardHeader>
                    <CardTitle>Confirm Multi-Country Extraction</CardTitle>
                    <CardDescription>Extract {selectedCountries.length} countries at country level (no state drill-down)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                      <div className="flex flex-wrap gap-1.5">
                        {selectedCountries.map((c) => {
                          const extracted = completedExtractions.some((ce) => ce.country === c.name);
                          return (
                            <Badge
                              key={`mc-${c.name}`}
                              variant="outline"
                              className={`text-xs ${extracted ? "border-emerald-500/40 text-emerald-400" : "border-slate-600"}`}
                            >
                              {extracted && (
                                <svg className="w-2.5 h-2.5 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                              {c.name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => { setStep(2); }} className="border-slate-700">Back</Button>
                      <Button data-testid="btn-confirm-multi-country" onClick={handleStartMultiCountry} className="flex-1 bg-emerald-600 hover:bg-emerald-500">
                        Confirm & Extract {selectedCountries.length} Countries
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* History */}
              <ExtractionHistory runs={extractionRuns} onRetry={handleRetry} retryingId={retryingId} />
            </TabsContent>

            <TabsContent value="verification">
              <DataVerification api={api} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}

// --- Error Banner (extracted for readability) ---
function ErrorBanner({ error, onDismiss }) {
  const labels = {
    oom: "Out of Memory", download_error: "Download Failed",
    server_busy: "Server Busy", download_timeout: "Timed Out",
    download_exhausted: "Retries Exhausted", connection_error: "Connection Error",
    parse_error: "Parse Error",
  };

  const isOom = error.error_type === "oom";
  const isServer = error.error_type === "server_busy" || error.error_type === "download_exhausted";
  const badgeClass = isOom || (!isServer && !error.error_type?.includes("download"))
    ? "bg-red-500/20 text-red-400"
    : "bg-amber-500/20 text-amber-400";

  return (
    <div data-testid="error-banner" className="rounded-lg border border-red-500/30 bg-red-950/40 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-red-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
          <div>
            <h4 className="font-medium text-red-300">Extraction Failed</h4>
            <p data-testid="error-message" className="text-sm text-red-400/80 mt-0.5">{error.message}</p>
          </div>
        </div>
        <Button data-testid="btn-dismiss-error" variant="ghost" size="sm" onClick={onDismiss} className="text-red-400/60 hover:text-red-300 -mt-1 -mr-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Badge data-testid="error-type-badge" className={`text-xs ${badgeClass}`}>
          {labels[error.error_type] || error.error_type}
        </Badge>
        {isOom && <span className="text-xs text-red-400/60">Raise EXTRACTION_MEM_LIMIT_MB or deploy to a larger server</span>}
        {isServer && <span className="text-xs text-amber-400/60">Geofabrik may be temporarily overloaded - try again later</span>}
      </div>
    </div>
  );
}

export default App;
